import { getScriptCreatorCap } from '../constants/trialLimits';

/** @param {{ status?: string, data?: object }} res */
export function parseTrialUsageResponse(res) {
  if (res?.status === 'success' && res?.data && typeof res.data === 'object') {
    return { ok: true, usage: res.data };
  }
  return { ok: false, usage: null };
}

/**
 * @param {object} usage - row from GET /api/trial-usage
 * @returns {{ allowed: boolean, message: string }}
 */
export function canStartAutomationGenerator(usage) {
  if (!usage) {
    return { allowed: false, message: 'Unable to verify credits. Please try again.' };
  }
  if (usage.is_expired) {
    return { allowed: false, message: 'Your plan has expired. Please upgrade to continue.' };
  }
  if (usage.can_start_script_creator === false) {
    return { allowed: false, message: 'Not enough credits to start AI generation (3 credits required). Please upgrade your plan.' };
  }
  return { allowed: true, message: '' };
}

/**
 * Replay start gate — same threshold as Script Manager (0.5 credits).
 * @param {object} usage
 * @returns {{ allowed: boolean, message: string }}
 */
export function canStartAutomationReplay(usage) {
  if (!usage) {
    return { allowed: false, message: 'Unable to verify credits. Please try again.' };
  }
  if (usage.is_expired) {
    return { allowed: false, message: 'Your plan has expired. Please upgrade to continue.' };
  }
  if (usage.can_start_script_manager === false) {
    return { allowed: false, message: 'Not enough credits to start replay (0.5 credits required). Please upgrade your plan.' };
  }
  return { allowed: true, message: '' };
}

/**
 * Agent WebSocket is shared by AI generation (script_creator) and replay (script_manager).
 * Allow opening the socket if either gate passes so replay is not blocked by the generator-only rule.
 */
export function canConnectAutomationStudioSocket(usage) {
  const gen = canStartAutomationGenerator(usage);
  const rep = canStartAutomationReplay(usage);
  if (gen.allowed || rep.allowed) {
    return { allowed: true, message: '' };
  }
  if (!usage) {
    return { allowed: false, message: gen.message };
  }
  if (usage.is_expired) {
    return { allowed: false, message: gen.message };
  }
  return {
    allowed: false,
    message:
      'Not enough credits for Automation Studio. Replay requires at least 0.5 credits; AI step generation requires 3 credits. Please upgrade your plan.',
  };
}

export function getEffectiveScriptCreatorCap(usage) {
  if (!usage || typeof usage !== 'object') return 0;
  const fromApi = Number(usage.script_creator_cap);
  if (Number.isFinite(fromApi) && fromApi >= 0) return fromApi;
  return getScriptCreatorCap(usage.credit_limit);
}

export function usageExceedsScriptCreatorCap(usage) {
  if (!usage || typeof usage !== 'object') return false;
  const used = Number(usage.credits_used) ?? 0;
  const cap = getEffectiveScriptCreatorCap(usage);
  return used >= cap;
}

/** Generator logs (test agent) vs replay logs (TestReplayAgent). */
export function isLlmCompletionLogMessage(message) {
  if (typeof message !== 'string') return false;
  return message.includes('LLM returned') || message.includes('LLM Response received');
}
